"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslations } from "use-intl";

import { TrendChartIcon } from "@/components/appIcons";
import { buildOfferHistoryChartModel } from "@/components/marketplace/buildOfferHistoryChartModel";
import { DealOfferLoadingSkeleton } from "@/components/marketplace/deal-offer-shared";
import { getDealOfferPriceHistoryQueryOptions } from "@/components/marketplace/deal-offers-query";
import type { DealOffer, PriceHistoryResult } from "@/components/marketplace/deal-offers-types";
import { OfferHistoryComparisonChart } from "@/components/marketplace/offer-history-comparison-chart";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";

type HistorySize = "compact" | "regular";

function EmptyPriceHistory({ monitoringStarted = false }: { monitoringStarted?: boolean }) {
  const t = useTranslations("shared.affiliates");
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border py-12 text-center">
      <TrendChartIcon className="text-3xl text-muted-foreground" />
      <p className="max-w-md text-sm text-muted-foreground">
        {t(monitoringStarted ? "priceHistoryMonitoringStarted" : "noPriceHistory")}
      </p>
    </div>
  );
}

function ListingHistoryScope({ result }: { result: PriceHistoryResult }) {
  const t = useTranslations("shared.affiliates");
  return (
    <p className="flex items-center gap-2 break-words text-xs text-muted-foreground">
      {result.providerLogoUrl ? (
        <img src={result.providerLogoUrl} alt="" className="h-5 max-w-20 object-contain" />
      ) : null}
      {t("priceHistoryListingScope", {
        provider: result.providerName || result.providerKey,
        listing: result.listingTitle ?? result.listingId,
      })}
    </p>
  );
}

function PriceHistoryBody({
  offerId,
  result,
  fallbackCurrency,
  emptyState,
  size,
}: {
  offerId: string;
  result: PriceHistoryResult;
  fallbackCurrency: string | null;
  emptyState: "default" | "monitoring_started";
  size: HistorySize;
}) {
  const chartModel = useMemo(
    () =>
      buildOfferHistoryChartModel({
        series: result.series,
        offerId,
        checkedAt: result.stats.lastCheckedAt,
        listingIntervals: result.listingIntervals,
      }),
    [result.series, offerId, result.stats.lastCheckedAt, result.listingIntervals],
  );

  if (chartModel.minimum === null) {
    return (
      <div className="space-y-3">
        <ListingHistoryScope result={result} />
        <EmptyPriceHistory monitoringStarted={emptyState === "monitoring_started"} />
      </div>
    );
  }

  return (
    <OfferHistoryComparisonChart
      model={chartModel}
      currency={result.stats.currency ?? fallbackCurrency}
      referencePrice={result.stats.dealReferencePrice}
      dealDeltaPercent={result.stats.dealDeltaPercent}
      checkedAt={result.stats.lastCheckedAt}
      available={chartModel.points.at(-1)?.minimum != null}
      size={size}
    />
  );
}

/**
 * Fetches and renders an offer's price history (chart + sparse states), without any modal
 * chrome so each surface owns the surrounding product, provider, price, and action hierarchy.
 */
export function OfferPriceHistory({
  offer,
  currency,
  emptyState = "default",
  size = "regular",
}: {
  offer: DealOffer;
  currency: string;
  emptyState?: "default" | "monitoring_started";
  size?: HistorySize;
}) {
  const { viewerId } = useViewerQueryState();
  const t = useTranslations("shared.affiliates");
  // Price history is recorded in the offer's own currency; prefer it over the
  // viewer's display currency so the lookup matches the stored observations.
  const queryCurrency = offer.priceHistoryCurrency ?? offer.currentCurrency ?? currency;
  const query = useQuery(
    getDealOfferPriceHistoryQueryOptions(
      {
        offerId: offer.offerId,
        currency: queryCurrency,
      },
      { viewerId },
    ),
  );
  const data = query.data as PriceHistoryResult | undefined;

  if (query.isLoading) return <DealOfferLoadingSkeleton />;
  if (query.isError)
    return (
      <p role="alert" className="text-sm text-destructive">
        {t("priceHistoryLoadError")}
      </p>
    );
  if (!data) return null;
  return (
    <PriceHistoryBody
      offerId={offer.offerId}
      result={data}
      fallbackCurrency={queryCurrency}
      emptyState={emptyState}
      size={size}
    />
  );
}
